import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import AiFieldSuggestion from './AiFieldSuggestion';
import PhotoThumb from '../PhotoThumb';
import { srOnly } from '../organic/motion';
import { coordText, formatMoment, pageStyles as ps } from './fieldFormat';
import { EDIT_HOLD_MS } from '../../utils/fieldOutbox';
import { withAiAnswer, withHandoffRequest } from '../../utils/fieldBatches';
import { acceptBlockedReason } from '../../utils/aiThresholds';
import { mayRequestAi, uploadForIdentification } from '../../utils/aiIdentify';
import { trapTab } from '../../utils/focusTrap';
import { reportTab } from '../../utils/issueReports';
import { focusElement } from '../../utils/useFocusOnOpen';
import { isReadOnlyRefusal } from '../../utils/viewAs';
import FieldOutline from './FieldOutline';
import { mayAnnotate, withOutline } from '../../utils/fieldAnnotations';
import { batchOutcome } from '../../utils/fieldMode';
import { can } from '../../utils/permissions';
import { newBatchThermal, thermalContentType } from '../../utils/thermal/upload';
import FieldThermalImport from '../thermal/FieldThermalImport';
import RareFindPrompt from '../handoffs/RareFindPrompt';
import { handoffBody, rareFindOf } from '../../utils/handoffs';

/**
 * The camera's post-capture summary (2026-10-16): what was just saved, and
 * what the AI makes of it, before anything is sent.
 *
 * Save on the camera (FieldCamera.js finish) keeps the batch on the phone,
 * held back from the outbox for EDIT_HOLD_MS, and opens this dialog over the
 * viewfinder. It shows:
 *
 *  - the AI's answer (AiFieldSuggestion + AiIdentifyPanel, compact): safety
 *    first, then the top name and its match percentage, then the ranked
 *    alternatives, each with "Use this name", and "None of these". Asked by
 *    itself when the person's "Ask AI automatically on this phone" switch is
 *    on (utils/fieldAutoAi.js), else on a tap. Nothing is pickable when the
 *    answer flags anything dangerous: the name is typed in Edit details.
 *  - the observation: when, where (and how surely), the photographs with
 *    their views, whether a habitat photo was taken, notes, mission, target.
 *
 * The answer and a chosen name are written to the batch as they come
 * (utils/fieldBatches.js withAiAnswer): the outbox then links the prediction
 * to the record it creates, and a chosen name as an AI-assisted ('ai_image')
 * identification (utils/fieldOutbox.js aiLinkBody). Each write keeps the
 * batch held; Done hands it back to the outbox (the camera's onDone), Edit
 * details to the batch editor (onEdit), which takes the hold over.
 *
 * A modal dialog like the app's others (ViewAsDialog): focus on its heading,
 * Tab kept inside, Escape and × are Done. The photo viewer it opens is a
 * dialog of its own inside this one, and keeps its own keys. Focus goes back
 * to Snap, which is the camera's business.
 */

// The camera's AI requests keep the session: an expired token here is said,
// not a sign-out out of the middle of a field walk (utils/api.js).
export const KEEP = Object.freeze({ keepSession: true });

export default function FieldCaptureSummary({
  store, outbox, userId, user, batch, mission = null, target = null,
  auto = true, onAuto, aiEnv = null, aiEnvPending = false, aiPaused = false, onAiError,
  upload = uploadForIdentification, onDone, onEdit,
}) {
  const { t } = useTranslation(['field', 'ai', 'viewAs', 'collections', 'thermal', 'handoffs']);
  const [live, setLive] = useState(batch);
  const [outlining, setOutlining] = useState(false);
  const [blobs, setBlobs] = useState({});
  const [urls, setUrls] = useState({});
  const [ready, setReady] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const [sentMeanwhile, setSentMeanwhile] = useState(false);
  const [error, setError] = useState('');
  const [announcement, setAnnouncement] = useState('');
  const [closing, setClosing] = useState(false);
  const [rareFind, setRareFind] = useState(null);
  // A request that went straight to the server, the batch sent meanwhile.
  const [handedOff, setHandedOff] = useState(null);
  const dialogRef = useRef(null);
  const headingRef = useRef(null);
  const openRef = useRef(true);
  const closingRef = useRef(false);
  const liveRef = useRef(batch);
  liveRef.current = live;
  const doneRef = useRef(null);
  const id = batch.id;

  const say = (words) => setAnnouncement(prev => (prev === words ? `${words}\u00a0` : words));

  // The batch as it is stored now: our own writes, a fix the camera gave it.
  useEffect(() => {
    let alive = true;
    const reread = () => store.get(userId, id).then((b) => { if (alive && b) setLive(b); }).catch(() => {});
    const off = store.subscribe ? store.subscribe(reread) : () => {};
    return () => { alive = false; off(); };
  }, [store, userId, id]);

  // The photographs from the device store (the camera's previews are gone).
  useEffect(() => {
    let alive = true;
    const made = [];
    (async () => {
      const found = {};
      const shown = {};
      for (const p of batch.photos || []) {
        const blob = await store.getBlob(userId, p.id).catch(() => null);
        if (!alive) return;
        if (!blob) continue;
        found[p.id] = blob;
        if (typeof URL.createObjectURL === 'function') {
          shown[p.id] = URL.createObjectURL(blob);
          made.push(shown[p.id]);
        }
      }
      setBlobs(found);
      setUrls(shown);
      setReady(true);
    })();
    return () => {
      alive = false;
      made.forEach(u => URL.revokeObjectURL?.(u));
    };
  }, [store, userId, id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Re-armed on every setup: StrictMode's dev double-mount runs the cleanup
    // below once before the dialog is really open.
    openRef.current = true;
    closingRef.current = false;
    focusElement(headingRef.current);
    const onKey = (e) => {
      // A dialog opened inside this one (the photo viewer) keeps its own keys.
      const owner = e.target?.closest?.('[role="dialog"]');
      if (owner && owner !== dialogRef.current) return;
      if (e.key === 'Escape') {
        if (!closingRef.current) doneRef.current?.();
        return;
      }
      trapTab(dialogRef.current, e, { extra: reportTab() });
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      openRef.current = false;
    };
  }, []);

  const done = async () => {
    if (closingRef.current) return;
    closingRef.current = true;
    openRef.current = false;
    setClosing(true);
    try {
      await onDone?.();
    } catch {
      closingRef.current = false;
      openRef.current = true;
      setClosing(false);
    }
  };
  doneRef.current = done;

  const edit = () => {
    if (closingRef.current) return;
    openRef.current = false;
    onEdit?.();
  };

  // The answer, or the name chosen from it, kept with the batch at once; the
  // hold renewed with it. A batch no longer waiting here takes nothing.
  // An answer that arrives after Done or Edit details is still kept -- the
  // outbox links it, the editor shows it -- without holding the batch any
  // longer; a pick is only ever made with the summary open.
  const persist = async (meta) => {
    const open = openRef.current;
    if (!open && meta?.accepted) return;
    const before = liveRef.current?.ai || null;
    let refused = false;
    let alreadyKept = false;
    try {
      const next = await store.update(userId, id, (current) => {
        alreadyKept = !!current?.ai?.aiId && current.ai.aiId === meta?.aiId;
        const proposed = withAiAnswer(current, meta, open
          ? { holdUntil: new Date(Date.now() + EDIT_HOLD_MS).toISOString() } : {});
        if (!proposed) refused = true;
        return proposed;
      });
      if (!open) return;
      if (refused || !next) {
        // Sent meanwhile. An answer kept with the batch before it went is
        // linked to the record, where a name can be chosen from it; one that
        // came too late is not, and has to be asked for there again.
        setSentMeanwhile(alreadyKept ? 'kept' : 'lost');
        return;
      }
      setLive(next);
      setError('');
      if (meta?.accepted) say(t('summary.picked', { taxon: meta.accepted.taxon_name }));
      else if (before?.aiId === meta?.aiId && before?.accepted) say(t('summary.pickCleared'));
      else if (meta?.top) {
        say(t('summary.aiAnswered', { taxon: meta.top.taxon_name, pct: Math.round((meta.top.confidence || 0) * 100) }));
      }
    } catch (e) {
      if (open) setError(isReadOnlyRefusal(e) ? t('viewAs:notKept') : t('summary.storeFailed'));
    }
  };

  // A request nobody tapped for ends in the dialog's own live region, so a
  // failure or the daily limit is heard, not only an answer.
  const failed = (e) => {
    onAiError?.(e);
    if (openRef.current) say(t(`ai:${e?.key || 'failed'}`));
  };

  const askedRarity = useRef(null);
  const heard = (result) => {
    if (result?.predictions) setBlocked(acceptBlockedReason(result));
    // The rare-find prompt (B6): a High-certainty answer naming a rare
    // species asks the collector, still standing by it, to collect it for
    // the lab. Asked once per answer; the server's scales decide.
    if (!result?.ai_id || askedRarity.current === result.ai_id) return;
    askedRarity.current = result.ai_id;
    rareFindOf(result, user, { config: KEEP }).then((found) => {
      if (askedRarity.current === result.ai_id) setRareFind(found);
    });
  };

  // A pickup or drop-off asked for from the prompt, kept with the batch (the
  // hold renewed) and sent by the outbox once the record is; none removes it.
  // Sent meanwhile, a request typed in the dialog is not lost: it goes to the
  // record on the server straight away (POST /handoffs takes a sent record),
  // and a refusal keeps the dialog open saying so. An answer that reached the
  // record too late to be linked to it is dropped, as the outbox drops it.
  const keepHandoff = async (request, aiId) => {
    let refused = false;
    let stored = null;
    const next = await store.update(userId, id, (current) => {
      stored = current;
      const proposed = withHandoffRequest(current, request, {
        aiId, holdUntil: new Date(Date.now() + EDIT_HOLD_MS).toISOString() });
      if (!proposed) refused = true;
      return proposed;
    });
    if (refused || !next) {
      const sampleId = stored?.serverSampleId;
      if (request && sampleId) {
        const ask = answer => api.post('/handoffs', handoffBody(request, sampleId, { aiId: answer }), KEEP);
        try {
          await ask(aiId).catch((e) => {
            if (!aiId || e?.response?.data?.code !== 'invalid_ai_id') throw e;
            return ask(null);
          });
        } catch (e) {
          throw new Error(t('handoffs:prompt.sentFailed', { why: errorText(e) }));
        }
        setHandedOff({ ...request, aiId: aiId || null });
        say(t('handoffs:prompt.saved'));
        return;
      }
      setSentMeanwhile(m => m || 'kept');
      throw new Error(t('summary.storeFailed'));
    }
    setLive(next);
    say(request ? t('handoffs:prompt.saved') : t('handoffs:prompt.removed'));
  };
  // "Collect it as a specimen": an Observation-mode batch sent as a
  // collection instead, so it is minted a code (utils/fieldMode.js).
  const collectInstead = async () => {
    const next = await store.update(userId, id, (current) => {
      if (!current || current.status === 'sent' || current.serverSampleId) return null;
      return { ...current, details: { ...(current.details || {}), observeOnly: false },
        holdUntil: new Date(Date.now() + EDIT_HOLD_MS).toISOString() };
    });
    if (!next) throw new Error(t('summary.storeFailed'));
    setLive(next);
    say(t('handoffs:prompt.collecting'));
  };

  const photos = live.photos || [];
  const details = live.details || {};
  // Never a guest, a read-only account or an executive: the server refuses their marks.
  const mayOutline = mayAnnotate(user);
  const saveOutline = async (outline) => {
    try {
      let kept = false;
      const next = await store.update(userId, id, (current) => {
        if (!current || current.status === 'sent') return null;
        kept = true;
        // Sent with the Pro drawer's marks on that photo, in one PUT (utils/fieldAnnotations.js).
        return withOutline(current, outline);
      });
      setOutlining(false);
      if (kept && next) {
        setLive(next);
        say(t('outline.saved'));
      } else {
        setError(t('summary.storeFailed'));
      }
    } catch (e) {
      setError(isReadOnlyRefusal(e) ? t('viewAs:notKept') : t('summary.storeFailed'));
    }
  };
  // The Thermal step: a radiometric file the thermal camera's app saved,
  // kept on the phone with the batch at once (the hold renewed), and sent by
  // the outbox after the photos, linked to the collection.
  const addThermal = async (file) => {
    const item = newBatchThermal({ name: file.name, size: file.size, contentType: thermalContentType(file),
      addedAt: new Date().toISOString() });
    try {
      await store.putBlob(userId, item.id, file);
      let kept = false;
      const next = await store.update(userId, id, (current) => {
        if (!current || current.status === 'sent' || current.serverSampleId) return null;
        kept = true;
        return { ...current, thermal: [...(current.thermal || []), item],
          holdUntil: new Date(Date.now() + EDIT_HOLD_MS).toISOString() };
      });
      if (kept && next) {
        setLive(next);
        say(t('thermal:field.added', { name: file.name }));
      } else {
        await store.removeBlobs(userId, [item.id]);
        setError(t('summary.storeFailed'));
      }
    } catch (e) {
      setError(isReadOnlyRefusal(e) ? t('viewAs:notKept') : t('summary.storeFailed'));
    }
  };
  const removeThermal = async (thermalId) => {
    try {
      const next = await store.update(userId, id, (current) => {
        if (!current || current.status === 'sent') return null;
        return { ...current, thermal: (current.thermal || []).filter(f => f.id !== thermalId || f.sent) };
      });
      if (next) {
        setLive(next);
        await store.removeBlobs(userId, [thermalId]);
      }
    } catch (e) {
      setError(isReadOnlyRefusal(e) ? t('viewAs:notKept') : t('summary.storeFailed'));
    }
  };
  const signedIn = outbox?.signedIn ? outbox.signedIn() : true;
  const mayAsk = mayRequestAi(user);
  const prodOnly = !!aiEnv?.prodOnly;
  const notConfigured = !prodOnly && aiEnv?.configured === false;
  const held = prodOnly || notConfigured || !signedIn || !!sentMeanwhile;
  // What stops or holds the AI, said once: the batch already gone, then no
  // session, then dev's prod-only, no AI service, the day's limit reached.
  let aiStatus = null;
  if (sentMeanwhile) {
    aiStatus = { key: 'sent-meanwhile', kept: sentMeanwhile,
      text: sentMeanwhile === 'kept' ? t('summary.sentMeanwhile') : t('summary.sentMeanwhileAskAgain') };
  } else if (!signedIn) aiStatus = { key: 'sign-in', text: t('summary.signInForAi') };
  else if (prodOnly) aiStatus = { key: 'prod-only', text: t('ai:prodOnly') };
  else if (notConfigured) aiStatus = { key: 'not-configured', text: t('ai:notConfigured') };
  else if (aiPaused && auto) aiStatus = { key: 'ai-paused', text: t('summary.aiPaused') };
  // The camera's warm-up has not answered yet: whether an answer can come at
  // all (dev, staging) is not known, so nothing goes up until it is.
  const waitingForEnv = !!aiEnvPending && !aiEnv;
  const aiPhotos = ready ? photos.map(p => ({ view: p.view, file: blobs[p.id] || null })).filter(p => p.file) : [];
  const viewable = photos.filter(p => urls[p.id])
    .map(p => ({ s3_url: urls[p.id], photo_type: p.view, photo_id: p.id, notes: p.note }));
  const habitatTaken = photos.some(p => p.view === 'habitat');
  const fromAi = !!live.ai?.accepted && !!details.taxon_name
    && String(details.taxon_name).trim().toLowerCase() === String(live.ai.accepted.taxon_name).trim().toLowerCase();
  const where = live.location ? coordText(live.location.lat, live.location.lng) : '';
  const accuracy = Number(live.location?.accuracy_m);
  const missionTitle = live.missionId
    ? (mission && String(mission.missionId) === String(live.missionId) && mission.title) || String(live.missionId)
    : '';
  const targetLabel = live.targetId
    ? (target && String(target.targetId) === String(live.targetId) && target.label) || t('camera.targetUnnamed')
    : '';
  const outcome = batchOutcome(live, can(user, 'specimen:register'));
  const mayCollect = outcome === 'observation' && !!details.observeOnly && can(user, 'specimen:register')
    && !live.serverSampleId && !live.specimenCode;

  return (
    <div className="hub-drawer-backdrop" data-testid="field-summary-backdrop">
      <style>{SUMMARY_CSS}</style>
      <aside ref={dialogRef} className="hub-drawer fm-summary" role="dialog" aria-modal="true"
        aria-labelledby="field-summary-title" aria-describedby="field-summary-intro" aria-busy={closing}
        data-testid="field-summary" data-batch={id} style={cs.aside}>
        <div className="hub-drawer__head">
          <h2 id="field-summary-title" ref={headingRef} tabIndex={-1} style={cs.title}>{t('summary.title')}</h2>
          <button type="button" onClick={done} aria-label={t('summary.close')} title={t('tip.summary.done')}
            data-testid="field-summary-close" style={cs.close}>
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <p id="field-summary-intro" style={cs.intro}>{t('summary.intro')}</p>
        {/* What Send will make of it, by the outbox's own rule (utils/fieldMode.js). */}
        <p style={cs.intro} data-testid="field-summary-outcome" data-outcome={outcome}>
          {t(`summary.outcome.${outcome}`)}
        </p>

        {mayAsk && (
          <section style={ps.section} aria-labelledby="field-summary-ai-title" data-testid="field-summary-ai-section">
            <div style={cs.sectionHead}>
              <h3 id="field-summary-ai-title" style={ps.h2}>{t('ai:field.title')}</h3>
              <button type="button" aria-pressed={!!auto} onClick={() => onAuto?.(!auto)} title={t('tip.summary.auto')}
                data-testid="field-summary-auto" style={cs.switchBtn}>
                <span>{t('summary.auto')}</span>
                <span style={auto ? cs.pillOn : cs.pillOff} aria-hidden="true">
                  {auto ? t('summary.autoOn') : t('summary.autoOff')}
                </span>
              </button>
            </div>
            {/* One status line, the one that matters most (docs/UX_AUDIT_2026-10-24.md, 13). */}
            {aiStatus && (
              <p role="note" style={aiStatus.key === 'sent-meanwhile' ? ps.note : ps.hint}
                data-testid={`field-summary-${aiStatus.key}`} data-kept={aiStatus.kept}>
                {aiStatus.text}
              </p>
            )}
            <AiFieldSuggestion testid="field-summary-ai" photos={aiPhotos} user={user} value={live.ai || null}
              onChange={persist} onResult={heard} onError={failed}
              autoAsk={!!auto && !aiPaused && !held && !waitingForEnv} disabled={held || waitingForEnv}
              pickAny compact requestConfig={KEEP} upload={upload} />
            {blocked && !sentMeanwhile && (
              <button type="button" onClick={edit} title={t('tip.summary.edit')} data-testid="field-summary-type-name"
                style={ps.secondary}>
                {t('summary.typeName')}
              </button>
            )}
            {!sentMeanwhile && (rareFind || live.handoff || handedOff) && (
              <RareFindPrompt find={rareFind} request={handedOff || live.handoff || null}
                location={live.location || null} sentNote={handedOff || live.handoffSent || live.status === 'sent' ? 'sent' : 'withRecord'}
                mayCollect={mayCollect} onCollect={collectInstead} onRequest={keepHandoff}
                onRemove={() => keepHandoff(null, null)} testId="field-summary-rare" />
            )}
          </section>
        )}

        <section style={{ ...ps.section, marginTop: 14 }} aria-labelledby="field-summary-obs-title">
          <h3 id="field-summary-obs-title" style={ps.h2}>{t('summary.observation')}</h3>
          <dl style={cs.list}>
            {details.taxon_name && (
              <div style={cs.row} data-testid="field-summary-name">
                <dt style={cs.term}>{t('identify.taxon')}</dt>
                <dd style={cs.value}>
                  <i dir="ltr">{details.taxon_name}</i>
                  {fromAi && <span style={cs.tag} data-testid="field-summary-ai-assisted">{t('summary.aiAssisted')}</span>}
                </dd>
              </div>
            )}
            <div style={cs.row}>
              <dt style={cs.term}>{t('editor.when')}</dt>
              <dd style={cs.value} data-testid="field-summary-when">{formatMoment(live.startedAt || live.createdAt)}</dd>
            </div>
            <div style={cs.row}>
              <dt style={cs.term}>{t('editor.where')}</dt>
              <dd style={cs.value} data-testid="field-summary-where">
                {where ? (
                  <>
                    <bdi dir="ltr">{where}</bdi>
                    {Number.isFinite(accuracy) && <span style={cs.muted}>{' · '}{t('camera.gpsOk', { m: Math.round(accuracy) })}</span>}
                  </>
                ) : <span style={cs.muted}>{t('summary.noLocation')}</span>}
              </dd>
            </div>
            <div style={cs.row}>
              <dt style={cs.term}>{t('editor.photos')}</dt>
              <dd style={cs.value}>
                <ul className="fm-summary-photos" style={cs.photos}>
                  {photos.map((p, i) => (
                    <li key={p.id} style={cs.photo} data-testid={`field-summary-photo-${i}`} data-view={p.view}>
                      <PhotoThumb photos={viewable} index={Math.max(0, viewable.findIndex(v => v.photo_id === p.id))}
                        disabled={!urls[p.id]} testId={`field-summary-photo-open-${i}`}
                        label={t('camera.trayPhoto', { n: i + 1, view: t(`views.${p.view}`) })}>
                        {urls[p.id]
                          ? <img src={urls[p.id]} alt={t('camera.trayPhoto', { n: i + 1, view: t(`views.${p.view}`) })}
                              style={cs.photoImg} />
                          : <span style={cs.photoEmpty}>{i + 1}</span>}
                      </PhotoThumb>
                      <span style={cs.photoCaption}>{t(`viewsShort.${p.view}`)}</span>
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
            <div style={cs.row}>
              <dt style={cs.term}>{t('views.habitat')}</dt>
              <dd style={cs.value} data-testid="field-summary-habitat" data-taken={habitatTaken}>
                {habitatTaken ? t('summary.habitatTaken') : t('summary.habitatMissing')}
                {details.substrate && (
                  <div>{t('place.substrate')}: {t(`collections:substrate.${details.substrate}`)}</div>
                )}
                {details.host_species && <div>{t('place.host')}: {details.host_species}</div>}
                {!details.substrate && !details.host_species && (
                  <div style={cs.muted}>{t('summary.habitatMore')}</div>
                )}
              </dd>
            </div>
            <div style={cs.row}>
              <dt style={cs.term}>{t('details.notesTitle')}</dt>
              <dd style={cs.value} data-testid="field-summary-notes">
                {details.general_notes
                  ? <span style={cs.notes}>{details.general_notes}</span>
                  : <span style={cs.muted}>{t('summary.noNotes')}</span>}
              </dd>
            </div>
            {missionTitle && (
              <div style={cs.row} data-testid="field-summary-mission">
                <dt style={cs.term}>{t('summary.mission')}</dt>
                <dd style={cs.value}><bdi>{missionTitle}</bdi></dd>
              </div>
            )}
            {targetLabel && (
              <div style={cs.row} data-testid="field-summary-target">
                <dt style={cs.term}>{t('summary.target')}</dt>
                <dd style={cs.value}><bdi>{targetLabel}</bdi></dd>
              </div>
            )}
          </dl>
        </section>

        {can(user, 'lab:observe') && (
          <div style={{ ...ps.section, marginTop: 14 }}>
            <FieldThermalImport value={live.thermal || []} onAdd={addThermal} onRemove={removeThermal}
              disabled={!!sentMeanwhile} headingAs="h3" headingStyle={ps.h2} testid="field-summary-thermal" />
          </div>
        )}

        {/* The tissue outline (Part III section 18): optional, for those who
            may annotate photographs (never a guest or a read-only account). */}
        {mayOutline && photos.some(p => urls[p.id]) && (
          <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" style={ps.secondary} onClick={() => setOutlining(true)}
              data-testid="field-summary-outline">{t('outline.open')}</button>
            {live.outline && <span data-testid="field-summary-outline-saved">{t('outline.saved')}</span>}
          </div>
        )}
        {outlining && (
          <FieldOutline photos={photos} urls={urls} initial={live.outline || null}
            onClose={() => setOutlining(false)} onSave={saveOutline} />
        )}

        {error && <p role="alert" style={{ ...ps.warn, marginTop: 12 }} data-testid="field-summary-error">{error}</p>}

        <div style={cs.footer}>
          <button type="button" onClick={edit} title={t('tip.summary.edit')} data-testid="field-summary-edit"
            style={ps.secondary}>
            {t('summary.edit')}
          </button>
          <button type="button" onClick={done} title={t('tip.summary.done')} aria-disabled={closing}
            data-testid="field-summary-done" style={{ ...ps.primary, opacity: closing ? 0.6 : 1 }}>
            {t('summary.done')}
          </button>
        </div>
        <div aria-live="polite" style={srOnly} data-testid="field-summary-live">{announcement}</div>
      </aside>
    </div>
  );
}

// A short landscape screen: smaller thumbnails, so Done stays in reach.
const SUMMARY_CSS = '@media (orientation: landscape) and (max-height: 500px){'
  + '.fm-summary-photos li{width:48px!important}'
  + '.fm-summary-photos img,.fm-summary-photos li>span:first-child{width:48px!important;height:48px!important}}';

const cs = {
  // Physical sides, so no RTL swap: a phone on its side keeps the controls
  // out of the rounded corner and from under the sensor housing.
  aside: {
    zIndex: 1, whiteSpace: 'normal', boxSizing: 'border-box', maxHeight: '100dvh', overscrollBehavior: 'contain',
    paddingTop: 'calc(18px + env(safe-area-inset-top))', paddingBottom: 0, display: 'flex', flexDirection: 'column',
    paddingLeft: 'max(18px, env(safe-area-inset-left))', paddingRight: 'max(18px, env(safe-area-inset-right))',
    textAlign: 'start',
  },
  title: { margin: 0, fontSize: 19, outline: 'none' },
  close: {
    minWidth: 44, minHeight: 44, padding: 0, border: 'none', background: 'none', fontSize: 26, lineHeight: 1,
    color: 'var(--c-text-muted-fg)', cursor: 'pointer',
  },
  intro: { margin: '4px 0 14px', fontSize: 14, color: 'var(--c-text-muted-fg)', lineHeight: 1.45 },
  sectionHead: { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between' },
  switchBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 44, padding: '0 10px', borderRadius: 999,
    border: '1px solid var(--c-border-strong-2)', background: 'var(--c-surface)', color: 'var(--c-text-2-fg)',
    font: 'inherit', fontSize: 13, cursor: 'pointer', textAlign: 'start',
  },
  pillOn: {
    padding: '2px 8px', borderRadius: 999, background: 'var(--c-primary-2)', color: 'var(--c-on-accent)',
    fontWeight: 700, fontSize: 12,
  },
  pillOff: {
    padding: '2px 8px', borderRadius: 999, background: 'var(--c-bg-subtle-3)', color: 'var(--c-text-muted-3-fg)',
    fontWeight: 700, fontSize: 12,
  },
  list: { margin: 0, display: 'flex', flexDirection: 'column', gap: 10 },
  row: { display: 'flex', flexDirection: 'column', gap: 2 },
  term: { fontSize: 13, fontWeight: 600, color: 'var(--c-text-body-2-fg)' },
  value: { margin: 0, fontSize: 15, color: 'var(--c-text-2-fg)', overflowWrap: 'anywhere' },
  muted: { color: 'var(--c-text-muted-2-fg)', fontSize: 14 },
  notes: { whiteSpace: 'pre-wrap' },
  tag: {
    marginInlineStart: 8, padding: '1px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600,
    background: 'var(--c-info-soft-4)', color: 'var(--c-info-2-fg)', border: '1px solid var(--c-info-soft-5)',
  },
  photos: { listStyle: 'none', margin: '4px 0 0', padding: 0, display: 'flex', flexWrap: 'wrap', gap: 8 },
  photo: { width: 64, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 },
  photoImg: { width: 64, height: 64, objectFit: 'cover', borderRadius: 8, display: 'block' },
  photoEmpty: {
    width: 64, height: 64, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--c-bg-subtle-3)',
  },
  photoCaption: { fontSize: 12, color: 'var(--c-text-muted-2-fg)', textAlign: 'center' },
  // Always in reach, however long the answer: Done and Edit at the foot.
  footer: {
    position: 'sticky', bottom: 0, display: 'flex', gap: 8, flexWrap: 'wrap',
    justifyContent: 'flex-end', marginTop: 14,
    marginLeft: 'calc(-1 * max(18px, env(safe-area-inset-left)))',
    marginRight: 'calc(-1 * max(18px, env(safe-area-inset-right)))',
    paddingTop: 12, paddingLeft: 'max(18px, env(safe-area-inset-left))',
    paddingRight: 'max(18px, env(safe-area-inset-right))',
    paddingBottom: 'calc(12px + env(safe-area-inset-bottom))', background: 'var(--c-surface)',
    borderTop: '1px solid var(--c-border-2)',
  },
};
